import { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import { Alert, Box, Button, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { cleanValues } from '../../utils/helper';
import {
  DatePickerUI,
  FormLabelUI,
  FormErrorUI,
  FormHeaderUI,
  FormButtonPanelUI,
  DeleteDialogUI,
  InputUI,
  SelectUI,
} from '../Interface';
import materialPlanService from '../../service/materialPlan.service';
import MaterialPlanFields from './MaterialPlanFields';
import MaterialPlanTabs from './MaterialPlanTabs';
import MaterialPlanItemFilter from './MaterialPlanItemFilter';
import { MaterialPlanBaseOfPlanList, MaterialPlanStatus } from '../../config/materialPlan';
import { defaultValues } from './MaterialPlanPage';

const MaterialPlanForm = ({
  selectedValues,
  setSelectedValues,
  viewField,
  setViewField,
  operatorsDisabled,
  setOperatorsDisabled,
  rowSelectionModel,
  setRowSelectionModel,
  setRefetchTable,
  user,
  canEdit,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonVisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [initValues, setInitValues] = useState({ ...defaultValues, dateFrom: dayjs() });
  const [itemCodeFilter, setItemCodeFilter] = useState(null);
  const [generatedFilterState, setGeneratedFilterState] = useState(null);

  const normalizeMaterialPlanLines = (lines = []) =>
    (Array.isArray(lines) ? lines : []).map((line) => ({
      ...line,
      ordernumber: line?.ordernumber ?? '',
    }));

  const parseItemFilter = (filterValue) => {
    if (!filterValue) return null;
    if (typeof filterValue === 'object') return filterValue;

    try {
      return JSON.parse(filterValue);
    } catch {
      return null;
    }
  };

  const stringifyItemFilter = (filterValue) => {
    if (!filterValue) return null;

    try {
      return JSON.stringify(filterValue);
    } catch {
      return null;
    }
  };

  const buildGeneratedFilterState = (values, currentItemCodeFilter = itemCodeFilter) => ({
    dateFrom: values.dateFrom ? dayjs(values.dateFrom).format('YYYY-MM-DD') : null,
    dateTo: values.dateTo ? dayjs(values.dateTo).format('YYYY-MM-DD') : null,
    mode: values.mode ?? null,
    reference: values.reference ?? null,
    itemCode: currentItemCodeFilter ?? null,
  });

  const normalizeStoredGeneratedFilterState = (storedFilterValue, values = {}) => {
    const parsedFilter = parseItemFilter(storedFilterValue);

    if (!parsedFilter) {
      return {
        dateFrom: values.dateFrom ? dayjs(values.dateFrom).format('YYYY-MM-DD') : null,
        dateTo: values.dateTo ? dayjs(values.dateTo).format('YYYY-MM-DD') : null,
        mode: values.mode ?? null,
        reference: values.reference ?? null,
        itemCode: null,
      };
    }

    const hasFullFilterShape =
      Object.prototype.hasOwnProperty.call(parsedFilter, 'dateFrom') ||
      Object.prototype.hasOwnProperty.call(parsedFilter, 'dateTo') ||
      Object.prototype.hasOwnProperty.call(parsedFilter, 'mode') ||
      Object.prototype.hasOwnProperty.call(parsedFilter, 'reference') ||
      Object.prototype.hasOwnProperty.call(parsedFilter, 'itemCode');

    if (hasFullFilterShape) {
      return {
        dateFrom: parsedFilter.dateFrom ?? (values.dateFrom ? dayjs(values.dateFrom).format('YYYY-MM-DD') : null),
        dateTo: parsedFilter.dateTo ?? (values.dateTo ? dayjs(values.dateTo).format('YYYY-MM-DD') : null),
        mode: parsedFilter.mode ?? values.mode ?? null,
        reference: parsedFilter.reference ?? values.reference ?? null,
        itemCode: parsedFilter.itemCode ?? null,
      };
    }

    return {
      dateFrom: values.dateFrom ? dayjs(values.dateFrom).format('YYYY-MM-DD') : null,
      dateTo: values.dateTo ? dayjs(values.dateTo).format('YYYY-MM-DD') : null,
      mode: values.mode ?? null,
      reference: values.reference ?? null,
      itemCode: parsedFilter,
    };
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues({ ...defaultValues, status: MaterialPlanStatus.OPENED, dateFrom: dayjs() });
      setItemCodeFilter(null);
      setGeneratedFilterState(null);
    }
  }, [selectedValues]);

  const valuesToForm = () => {
    const { id, planNo, description, mode, dateFrom, dateTo, reference, materialPlanLine, itemFilter, status } =
      selectedValues;
    const normalizedGeneratedFilterState = normalizeStoredGeneratedFilterState(itemFilter, selectedValues);
    const parsedItemFilter = normalizedGeneratedFilterState.itemCode;

    setItemCodeFilter(parsedItemFilter);
    setGeneratedFilterState(normalizedGeneratedFilterState);
    setInitValues({
      id: id ?? null,
      planNo: planNo ?? '',
      description: description ?? '',
      mode: mode ?? null,
      dateFrom: dateFrom ? dayjs(dateFrom) : null,
      dateTo: dateTo ? dayjs(dateTo) : null,
      reference: reference ?? '',
      status: status ?? selectedValues.materialPlanStatus ?? MaterialPlanStatus.OPENED,
      itemFilter: itemFilter ?? null,
      materialPlanLine: normalizeMaterialPlanLines(materialPlanLine),
    });
  };

  const validationSchema = Yup.object().shape({
    planNo: Yup.string().nullable(),
    description: Yup.string().nullable(),
    mode: Yup.string().required(),
    dateFrom: Yup.date().required(),
    dateTo: Yup.date().required(),
    reference: Yup.string().nullable(),
    status: Yup.string().nullable(),
  });

  const handleGenerate = (values, setFieldValue) => {
    const currentGeneratedFilterState = buildGeneratedFilterState(values);
    const body = {
      dateFrom: currentGeneratedFilterState.dateFrom || undefined,
      dateTo: currentGeneratedFilterState.dateTo || undefined,
      BaseOfPlan: currentGeneratedFilterState.mode || undefined,
      reference: currentGeneratedFilterState.reference || undefined,
      ...(currentGeneratedFilterState.itemCode ? { itemCode: currentGeneratedFilterState.itemCode } : {}),
    };
    materialPlanService
      .getUnplannedDemand(body)
      .then((data) => {
        const lines = Array.isArray(data) ? data : data.rows ?? [];
        setGeneratedFilterState(currentGeneratedFilterState);
        setFieldValue('materialPlanLine', normalizeMaterialPlanLines(lines));
        addNotification({
          content:
            lines.length > 0
              ? `Generálás kész – ${lines.length} sor betöltve.`
              : 'Generálás kész – nincs találat a szűrési feltételekre.',
          type: lines.length > 0 ? NotificationType.SUCCESS : NotificationType.WARNING,
        });
      })
      .catch(() => {
        addNotification({ content: 'Generálás sikertelen!', type: NotificationType.ERROR });
      });
  };

  const handleSubmit = (values) => {
    if (values.id) {
      handleUpdateObject(values);
    } else {
      handleInsertObject(values);
    }
  };

  const handleInsertObject = (values) => {
    const effectiveGeneratedFilterState = generatedFilterState ?? buildGeneratedFilterState(values);
    const insertObject = {
      ...cleanValues({
        ...values,
        mode: effectiveGeneratedFilterState.mode ?? values.mode,
        reference: effectiveGeneratedFilterState.reference ?? values.reference,
        dateFrom: effectiveGeneratedFilterState.dateFrom
          ? dayjs(effectiveGeneratedFilterState.dateFrom)
          : values.dateFrom,
        dateTo: effectiveGeneratedFilterState.dateTo ? dayjs(effectiveGeneratedFilterState.dateTo) : values.dateTo,
      }),
      itemFilter: stringifyItemFilter(effectiveGeneratedFilterState),
      createdBy: user.userId,
    };

    materialPlanService.createItem(insertObject).then((data) => {
      addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
      setSelectedValues(data);
      setRowSelectionModel(data.id);
      setRefetchTable(true);
      setAddField(true);
      setViewField(true);
      setEditButtonVisible(true);
    });
  };

  const handleUpdateObject = (values) => {
    const effectiveGeneratedFilterState = generatedFilterState ?? buildGeneratedFilterState(values);
    const updateObject = {
      ...cleanValues({
        ...values,
        mode: effectiveGeneratedFilterState.mode ?? values.mode,
        reference: effectiveGeneratedFilterState.reference ?? values.reference,
        dateFrom: effectiveGeneratedFilterState.dateFrom
          ? dayjs(effectiveGeneratedFilterState.dateFrom)
          : values.dateFrom,
        dateTo: effectiveGeneratedFilterState.dateTo ? dayjs(effectiveGeneratedFilterState.dateTo) : values.dateTo,
      }),
      itemFilter: stringifyItemFilter(effectiveGeneratedFilterState),
      updatedBy: user.userId,
    };

    materialPlanService.updateItem(updateObject).then((data) => {
      addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
      setSelectedValues(data);
      setRefetchTable(true);
      setAddField(true);
      setViewField(true);
      setEditButtonVisible(true);
    });
  };

  const handleRemoveElement = () => {
    materialPlanService.deleteItem(selectedValues.id).then(() => {
      addNotification({ content: 'Sikeres törlés!', type: NotificationType.WARNING });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({ ...defaultValues });
      setInitValues({ ...defaultValues, dateFrom: dayjs() });
    });
  };

  const onAddClick = () => {
    setInitValues({ ...defaultValues, status: MaterialPlanStatus.OPENED, dateFrom: dayjs() });
    setSelectedValues({});
    setRowSelectionModel([]);
    setViewField(false);
    setAddField(false);
    setItemCodeFilter(null);
    setGeneratedFilterState(null);
  };

  return (
    <>
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <FormHeaderUI
            addClick={onAddClick}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonVisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            deleteClick={() => setShowDeleteDialog(true)}
            deleteDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />
        </Box>

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, resetForm }) => {
            const currentFilterState = buildGeneratedFilterState(values, itemCodeFilter);
            const hasGeneratedSnapshot = !!generatedFilterState;
            const isLoadedPlan = !!selectedValues?.id;
            const showGeneratedFilterInfo = !isLoadedPlan;
            const generatedFilterChanged = hasGeneratedSnapshot && !isEqual(generatedFilterState, currentFilterState);

            return (
              <Form>
                {/* --- Filter --- */}
                <Box sx={{ px: 2, pt: 2, pb: 2, width: '100%', overflowX: 'hidden' }}>
                  <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                    <Grid item xs={12} sx={{ pl: '0 !important', pt: '0 !important' }}>
                      <MaterialPlanItemFilter
                        value={itemCodeFilter}
                        onChange={setItemCodeFilter}
                        disabled={viewField || !!values.id}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePickerUI
                        label={<FormLabelUI text="Dátumtól" />}
                        value={values.dateFrom}
                        onChange={(newVal) => setFieldValue('dateFrom', newVal)}
                        helperText={<FormErrorUI message={errors.dateFrom} />}
                        disabled={viewField || !!values.id}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePickerUI
                        label={<FormLabelUI text="Dátumig" />}
                        value={values.dateTo}
                        onChange={(newVal) => setFieldValue('dateTo', newVal)}
                        helperText={<FormErrorUI message={errors.dateTo} />}
                        disabled={viewField || !!values.id}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        name="mode"
                        component={SelectUI}
                        fullWidth
                        option={MaterialPlanBaseOfPlanList}
                        value={values.mode ?? ''}
                        onChange={(e) => setFieldValue('mode', e.target.value || null)}
                        label={<FormLabelUI text="Terv alapja" />}
                        helperText={<FormErrorUI message={errors.mode} />}
                        disabled={viewField || !!values.id}
                        variant="standard"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Field
                        type="text"
                        name="reference"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Hivatkozás" />}
                        helperText={<FormErrorUI message={errors.reference} />}
                        variant="standard"
                        disabled={viewField || !!values.id}
                        InputLabelProps={{ shrink: values.reference !== '' }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center' },
                        alignItems: { xs: 'center' },
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SearchIcon />}
                        onClick={() => handleGenerate(values, setFieldValue)}
                        disabled={viewField || !!values.id || !values.mode || !values.dateFrom || !values.dateTo}
                      >
                        Generálás
                      </Button>
                    </Grid>
                    {showGeneratedFilterInfo && (
                      <Grid item xs={12}>
                        <Alert
                          severity={hasGeneratedSnapshot && generatedFilterChanged ? 'warning' : 'info'}
                          sx={{ mt: 1 }}
                        >
                          {hasGeneratedSnapshot && generatedFilterChanged
                            ? 'A szűrés a generálás óta módosult. Mentéskor továbbra is a legutóbb generált állapot kerül rögzítésre.'
                            : 'Mentéskor a legutóbb generált szűrési állapot kerül rögzítésre.'}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* --- Header fields --- */}
                <MaterialPlanFields viewField={viewField} />

                {/* --- Tabs with lines --- */}
                <MaterialPlanTabs disabled={viewField} />

                <FormButtonPanelUI
                  onCancelClick={() => {
                    setViewField(true);
                    setEditButtonVisible(true);
                    setAddField(true);
                    resetForm();
                    valuesToForm();
                  }}
                  disabled={editButtonVisible && addField}
                />
              </Form>
            );
          }}
        </Formik>
      </Box>

      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default MaterialPlanForm;
